import React from 'react';
import { Plus, X, Edit2, Check } from 'lucide-react';
import { FileTag } from '@/types/file';

interface TagManagerProps {
  tags: FileTag[];
  onCreateTag: (tag: Omit<FileTag, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateTag: (id: string, updates: Partial<FileTag>) => void;
  onDeleteTag: (id: string) => void;
}

export function TagManager({ tags, onCreateTag, onUpdateTag, onDeleteTag }: TagManagerProps) {
  const [newTagName, setNewTagName] = React.useState('');
  const [newTagColor, setNewTagColor] = React.useState('#3B82F6');
  const [editingId, setEditingId] = React.useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTagName.trim()) {
      onCreateTag({
        name: newTagName.trim(),
        color: newTagColor,
      });
      setNewTagName('');
    }
  };

  const handleUpdate = (id: string, name: string, color: string) => {
    onUpdateTag(id, { name: name.trim(), color, updatedAt: new Date() });
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <input
          type="text"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit(e);
            }
          }}
          placeholder="New tag name"
          className="flex-grow rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
        <input
          type="color"
          value={newTagColor}
          onChange={(e) => setNewTagColor(e.target.value)}
          className="w-12 h-10 rounded cursor-pointer"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!newTagName.trim()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Tag
        </button>
      </div>

      <div className="space-y-2">
        {tags.map((tag) => (
          <div
            key={tag.id}
            className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm"
          >
            {editingId === tag.id ? (
              <div className="flex items-center space-x-4 flex-grow">
                <input
                  type="text"
                  defaultValue={tag.name}
                  className="flex-grow rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdate(
                        tag.id,
                        (e.target as HTMLInputElement).value,
                        tag.color
                      );
                    }
                  }}
                />
                <input
                  type="color"
                  defaultValue={tag.color}
                  className="w-10 h-8 rounded cursor-pointer"
                  onChange={(e) => {
                    handleUpdate(tag.id, tag.name, e.target.value);
                  }}
                />
                <button
                  onClick={() => setEditingId(null)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <Check className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="text-gray-900">{tag.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingId(tag.id)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDeleteTag(tag.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}